import { useEffect, useMemo, useState } from 'react';
import { Title, useDataProvider, useNotify, useRedirect } from 'react-admin';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Button,
    Divider,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupsIcon from '@mui/icons-material/Groups';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import ReviewsIcon from '@mui/icons-material/Reviews';
import PetsIcon from '@mui/icons-material/Pets';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const PERIODS = [
    { value: 'today', label: 'Сегодня' },
    { value: 'week', label: 'Неделя' },
    { value: 'month', label: 'Месяц' },
    { value: 'all', label: 'Все время' },
];

const METRIC_CARD_STYLES = {
    height: '100%',
    borderRadius: 3.5,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 8px 20px rgba(16, 40, 26, 0.06)',
    overflow: 'hidden',
    transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 28px rgba(16, 40, 26, 0.1)',
        borderColor: 'primary.main',
    },
    '&:focus-within': {
        borderColor: 'primary.main',
        boxShadow: '0 0 0 3px rgba(21, 128, 61, 0.14), 0 12px 28px rgba(16, 40, 26, 0.1)',
    },
};

const getPeriodCaption = (period) => {
    if (period === 'today') return 'за текущий день';
    if (period === 'week') return 'за 7 дней';
    if (period === 'month') return 'за 30 дней';
    return 'за все время';
};

const StatCard = ({ title, value, caption, icon, iconBg, onClick, actionLabel }) => (
    <Card sx={METRIC_CARD_STYLES}>
        <CardActionArea
            onClick={onClick}
            aria-label={actionLabel}
            sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'stretch',
                '&:focus-visible': { outline: 'none' },
                '&:hover .dashboard-metric-arrow': { transform: 'translateX(3px)', color: 'primary.main' },
            }}
        >
            <CardContent sx={{ p: 2.5, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                            {title}
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                lineHeight: 1.2,
                                fontFamily: '"Fira Code", Menlo, monospace',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {caption}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            color: 'white',
                            backgroundColor: iconBg,
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
                <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Открыть раздел</Typography>
                    <ArrowForwardIcon
                        className="dashboard-metric-arrow"
                        sx={{ fontSize: 16, transition: 'transform 180ms ease, color 180ms ease' }}
                    />
                </Box>
            </CardContent>
        </CardActionArea>
    </Card>
);

export const HomeDashboard = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const [period, setPeriod] = useState('today');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        new_users: 0,
        total_users: 0,
        new_appeals: 0,
        reviews: 0,
    });

    useEffect(() => {
        let active = true;

        const loadStats = async () => {
            setLoading(true);
            try {
                const { data } = await dataProvider.getOne('dashboard-stats', {
                    id: 'current',
                    meta: { period },
                });
                if (active) {
                    setStats(data);
                }
            } catch (error) {
                if (active) {
                    notify(`Не удалось загрузить статистику: ${error.message}`, { type: 'error' });
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadStats();

        return () => {
            active = false;
        };
    }, [dataProvider, notify, period]);

    const periodCaption = useMemo(() => getPeriodCaption(period), [period]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, md: 11 }, maxWidth: 1600, mx: 'auto' }}>
            <Title title="Главная" />

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                        Рабочее пространство
                    </Typography>
                    <Typography variant="h4">Добро пожаловать</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ключевые показатели и быстрый доступ к разделам
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={period}
                    onChange={(_, value) => {
                        if (value) {
                            setPeriod(value);
                        }
                    }}
                >
                    {PERIODS.map((item) => (
                        <ToggleButton key={item.value} value={item.value}>
                            {item.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            title="Новые пользователи"
                            value={stats.new_users ?? 0}
                            caption={periodCaption}
                            icon={<PersonAddAlt1Icon fontSize="small" />}
                            iconBg="#15803D"
                            onClick={() => redirect('/guest-users')}
                            actionLabel="Открыть новых пользователей в разделе «Гости и обращения»"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            title="Все пользователи"
                            value={stats.total_users ?? 0}
                            caption="текущее количество"
                            icon={<GroupsIcon fontSize="small" />}
                            iconBg="#059669"
                            onClick={() => redirect('/guest-users')}
                            actionLabel="Открыть всех пользователей в разделе «Гости и обращения»"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            title="Новые обращения"
                            value={stats.new_appeals ?? 0}
                            caption="непрочитанные обращения"
                            icon={<MarkEmailUnreadIcon fontSize="small" />}
                            iconBg="#D97706"
                            onClick={() => redirect('/guest-users')}
                            actionLabel="Открыть новые обращения"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            title="Отзывы"
                            value={stats.reviews ?? 0}
                            caption={periodCaption}
                            icon={<ReviewsIcon fontSize="small" />}
                            iconBg="#0284C7"
                            onClick={() => redirect('/external-reviews')}
                            actionLabel="Открыть отзывы"
                        />
                    </Grid>
                </Grid>
            )}

            <Card sx={{ mt: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Быстрые действия</Typography>
                            <Typography variant="body2" color="text.secondary">Частые задачи — в один переход</Typography>
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={1.5}>
                        {[
                            { label: 'Животные', caption: 'Каталог и карточки', path: '/animals', icon: <PetsIcon /> },
                            { label: 'Новости', caption: 'Публикации зоопарка', path: '/news', icon: <NewspaperIcon /> },
                            { label: 'Квесты', caption: 'Задачи и награды', path: '/quests', icon: <EmojiEventsIcon /> },
                            { label: 'Обращения', caption: 'Сообщения гостей', path: '/guest-users', icon: <SupportAgentIcon /> },
                            { label: 'Отзывы', caption: 'Все публичные источники', path: '/external-reviews', icon: <ReviewsIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} sm={6} lg={3} key={item.path}>
                                <Button
                                    fullWidth
                                    color="inherit"
                                    onClick={() => redirect(item.path)}
                                    sx={{
                                        minHeight: 72,
                                        px: 1.5,
                                        justifyContent: 'flex-start',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: 'background.default',
                                        '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                                    }}
                                >
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', color: 'primary.main', backgroundColor: 'action.selected', flexShrink: 0, mr: 1.5 }}>
                                        {item.icon}
                                    </Box>
                                    <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">{item.caption}</Typography>
                                    </Box>
                                    <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};
